<!--
  Pagination — Pineapple 톤
  ────────────────────────────
  · 기존 API 그대로 유지: props currentPage / totalPages, emit update:currentPage
  · MD MaterialPagination/Item 의존 제거
  · 페이지 수가 많을 때 ··· 으로 축약 (현재 페이지 주변 ±2 + 첫/끝)
-->
<template>
  <nav class="p-pagination" aria-label="Pagination">
    <button
      class="p-pagination__btn"
      :disabled="currentPage === 1"
      :aria-label="'이전 페이지'"
      @click="prevPage"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>

    <template v-for="(p, i) in visiblePages" :key="i">
      <span v-if="p === '...'" class="p-pagination__ellipsis">…</span>
      <button
        v-else
        class="p-pagination__btn"
        :class="{ 'is-active': p === currentPage }"
        :aria-current="p === currentPage ? 'page' : undefined"
        @click="changePage(p)"
      >{{ p }}</button>
    </template>

    <button
      class="p-pagination__btn"
      :disabled="currentPage === totalPages"
      :aria-label="'다음 페이지'"
      @click="nextPage"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  </nav>
</template>

<script>
export default {
  name: 'Pagination',
  props: {
    currentPage: { type: Number, required: true },
    totalPages:  { type: Number, required: true },
    /** 현재 페이지 좌우로 표시할 페이지 수 (기본 2개씩) */
    siblingCount: { type: Number, default: 2 },
  },
  emits: ['update:currentPage'],
  computed: {
    /** [1, '...', 4, 5, 6, 7, 8, '...', 20] 같은 페이지 배열 생성 */
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const sib = this.siblingCount;

      // 표시 가능한 최대 페이지 수: 첫/끝 + 좌우 sibling + 현재 + 양옆 dots
      const maxVisible = sib * 2 + 5;
      if (total <= maxVisible) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      const left = Math.max(current - sib, 2);
      const right = Math.min(current + sib, total - 1);
      const showLeftDots = left > 2;
      const showRightDots = right < total - 1;

      const pages = [1];
      if (showLeftDots) pages.push('...');
      for (let i = left; i <= right; i++) pages.push(i);
      if (showRightDots) pages.push('...');
      pages.push(total);
      return pages;
    },
  },
  methods: {
    changePage(page) {
      if (page === this.currentPage) return;
      this.$emit('update:currentPage', page);
    },
    prevPage() {
      if (this.currentPage > 1) this.$emit('update:currentPage', this.currentPage - 1);
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.$emit('update:currentPage', this.currentPage + 1);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-pagination {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: $p-font-body;

  &__btn {
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    border: 1px solid $p-line;
    background: $p-panel;
    border-radius: 6px;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink2;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled):not(.is-active) {
      background: $p-bg;
      color: $p-ink;
      border-color: $p-line-hard;
    }
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    &.is-active {
      background: $p-ink;
      color: $p-point;
      border-color: $p-ink;
    }

    svg {
      display: block;
      stroke-width: 2.2;
    }
  }

  &__ellipsis {
    min-width: 24px;
    text-align: center;
    color: $p-ink-soft;
    font-family: $p-font-mono;
    font-size: 13px;
    user-select: none;
  }
}
</style>
